import React, { useEffect, useState } from "react";
import { makeStyles } from '@material-ui/core/styles';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import DefaultScrollWithHeaderContent from "../../../../utils/DefaultScrollWithHeaderContent";
import MyTable from '../../../../utils/MyTable';

import { useDispatch, useSelector } from "react-redux";

//Import excel
import { forwardRef, useImperativeHandle, useMemo } from "react";
import allActions from "../../../../../actions";
import { headerSearch } from "../../../../../assets/styles/unoTheme";
import { applyMaskAmount, getFirstAndEndDateByMonthAndYear } from "../../../../utils/utils";
import { getDataToReturnsByAssetsReport, getRawDataToTargetsReport } from "../../../../../controllers/ReportController";
import { getRegimeLabel, parseRegimeValue } from "../../../../utils/accountRegimes";

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column'
    },
    green: {
        color: 'green'
    },
    red: {
        color: 'red',
    },
    headerSearch: headerSearch,
    inputSearch: {
        width: "300px",
        background: "white"
    },
    toStickyHeader: {
        display: 'flex',
        height: '100%',
    },
    tableColumn: {
        display: 'flex',
        justifyContent: "center",
        alignItems: 'center',
    }
});

const NO_DATA = "-";

//Campos da meta atuarial conforme o indexador escolhido pelo cliente (use_ipca)
const TARGET_FIELDS = {
    month: { ipca: 'taxMonthIpca', inpc: 'taxMonthInpc' },
    year: { ipca: 'taxAcumIpcaYear', inpc: 'taxAcumInpcYear' },
};

function formatTargetValue(target, kind) {

    if (!target) return null;

    const fields = TARGET_FIELDS[kind];
    const value = parseFloat(target.use_ipca ? target[fields.ipca] : target[fields.inpc]);

    return Number.isFinite(value) ? applyMaskAmount(value) + "%" : null;
}

/**
 * Monta a linha de meta atuarial ancorada no mês/ano selecionados no cabeçalho e nos
 * mesmos anos usados nas colunas da tabela (data.years), ao invés de indexar por posição.
 */
export function buildMapTargets(dataTargets, years, selectedMonth, selectedYear) {

    const month = Number(selectedMonth);
    const year = Number(selectedYear);

    const targetsByYear = new Map((dataTargets ?? []).map(el => [Number(el.year), el]));

    const monthsOfYear = (theYear) => targetsByYear.get(Number(theYear))?.finalTargetsToShow ?? [];

    //Última meta do ano dentro do limite do período (o array vem em ordem crescente de mês)
    const findTargetInYear = (theYear, monthLimit) => monthsOfYear(theYear)
        .reduce((acc, entry) => (Number(entry.month) <= monthLimit
            && (!acc || Number(entry.month) > Number(acc.month))) ? entry : acc, null);

    //A meta do mês precisa ser a do mês selecionado, e não a do último mês fechado do cliente
    const monthTarget = monthsOfYear(year).find(entry => Number(entry.month) === month);

    //Cada coluna de ano usa a mesma janela dos ativos: o ano selecionado acumula até o mês
    //selecionado (YTD) e os anos anteriores acumulam o ano cheio
    const yearTarget = (theYear) => theYear == null
        ? null
        : findTargetInYear(theYear, Number(theYear) === year ? month : 12);

    return {
        month: formatTargetValue(monthTarget, 'month'),
        yearBefore1: formatTargetValue(yearTarget(years?.[0]), 'year'),
        yearBefore2: formatTargetValue(yearTarget(years?.[1]), 'year'),
        yearBefore3: formatTargetValue(yearTarget(years?.[2]), 'year'),
    };
}

/**
 * Agrupa os ativos por artigo. Usada tanto pela tabela quanto pelo PDF, para que as duas
 * apresentações não voltem a divergir.
 *
 * Um ativo pode constar em mais de um artigo (quando mudou de enquadramento entre os períodos);
 * nesse caso ele é listado uma única vez, no primeiro artigo em que aparece, para evitar confusão.
 */
export function buildArticleGroups(data) {

    if (!data?.articles?.length || !data?.formatted?.length) return [];

    const alreadyIncluded = [];
    const groups = [];

    data.articles.forEach(article => {

        const assets = data.formatted.filter(el => el.multipleArticles?.includes(article)
            && !alreadyIncluded.includes(el.id));

        if (!assets.length) return;

        assets.forEach(el => alreadyIncluded.push(el.id));
        groups.push({ article, assets });
    });

    return groups;
}

const AssetsTab = forwardRef((props, ref) => {

    const classes = useStyles();
    const dispatch = useDispatch();

    const { segregationSelected } = props;
    const accountRegimeId = parseRegimeValue(segregationSelected);

    const clientOn = useSelector(state => state.clientOn)
    const [data, setData] = useState([]);
    const [mapTargets, setMapTargets] = useState(null);

    const [innerLoad, setInnerLoad] = useState({


        emptyMessage: "",
        loadMessage: "Carregando retornos globais...",
        size: "small"

    })

    async function loadData(client, regimeId, isCancelled) {

        //Enquanto houver uma carga mais nova, as mensagens de progresso desta não devem aparecer
        const setInnerLoadIfCurrent = (value) => {
            if (!isCancelled()) setInnerLoad(value);
        };

        const { startDate, endDate } = getFirstAndEndDateByMonthAndYear(client.selectedPortfolioMonth, client.selectedPortfolioYear);
        const { success, data } = await getDataToReturnsByAssetsReport(client, startDate, endDate, false, setInnerLoadIfCurrent, false, regimeId);

        //Outra segregação/cliente foi selecionado enquanto esta carga estava em andamento
        if (isCancelled()) return;

        if (!success) {
            setInnerLoad({
                emptyMessage: "Não foi possível carregar os retornos por ativo",
            });
            return;
        }

        setInnerLoad((prev) => ({ ...prev, loadMessage: "Obtendo metas atuariais..." }));

        //A falha na obtenção das metas não deve impedir a exibição dos retornos
        const { success: successTargets, data: dataTargets } = await getRawDataToTargetsReport(client, 0, client.selectedPortfolioYear);

        if (isCancelled()) return;

        setInnerLoad(buildArticleGroups(data).length
            ? null
            : { emptyMessage: "Nenhum ativo encontrado no período para a segregação selecionada" });
        setData(data);
        setMapTargets(buildMapTargets(
            successTargets ? dataTargets : [],
            data?.years,
            client.selectedPortfolioMonth,
            client.selectedPortfolioYear));
    }

    function makeReport() {
        const reportType = 'myReturnsByAssets';

        const reportData = [];

        buildArticleGroups(data).forEach(({ article, assets }) => {

            reportData.push({
                isArticleLine: true,
                article: article,
            })
            reportData.push({
                isHeader1Line: true,
                year1: data?.years?.[0],
                year2: data?.years?.[1],
                year3: data?.years?.[2]
            })
            reportData.push({
                isHeader2Line: true,
            })

            assets.forEach(row => {

                reportData.push({
                    fund_name: row.fund_name,
                    month2: row.month.rentPercent,
                    month1: row.month.rentMoney,
                    yearBefore12: row.year1.rentPercent,
                    yearBefore11: row.year1.rentMoney,
                    yearBefore22: row.year2.rentPercent,
                    yearBefore21: row.year2.rentMoney,
                    yearBefore32: row.year3.rentPercent,
                    yearBefore31: row.year3.rentMoney,
                })

            });

            reportData.push({
                isTargetsLine: true,
                month: mapTargets?.month ?? NO_DATA,
                year1: mapTargets?.yearBefore1 ?? NO_DATA,
                year2: mapTargets?.yearBefore2 ?? NO_DATA,
                year3: mapTargets?.yearBefore3 ?? NO_DATA,
            })

        });

        dispatch(allActions.reportLoadActions.setReportLoad({
            show: true,
            reportType,
            reportData: reportData,
            regimeLabel: accountRegimeId ? getRegimeLabel(accountRegimeId) : null,
            message: 'Gerando relatório...'
        }));
    }

    useImperativeHandle(ref, () => ({
        doPdfReport() {
            makeReport();
        }
    }));

    const headAndBodyTable = useMemo(() => {

        const groups = buildArticleGroups(data);
        if (!groups.length) return [];

        const nodes = [];

        groups.forEach(({ article, assets }, index) => {

            nodes.push(
                <TableHead key={`head-article-${index}`}>
                    <TableRow key={`head1-article-${index}`} className="smallPadding mainColor">
                        <TableCell align="center" colSpan={9}>
                            {article}
                        </TableCell>

                    </TableRow>
                    <TableRow key={`head2-article-${index}`} className="smallPadding">
                        <TableCell align="left">

                        </TableCell>
                        <TableCell align="center" colSpan={2}>
                            Mês
                        </TableCell>
                        <TableCell align="center" colSpan={2}>
                            {data?.years?.[0]}
                        </TableCell>
                        <TableCell align="center" colSpan={2}>
                            {data?.years?.[1]}
                        </TableCell>
                        <TableCell align="center" colSpan={2}>
                            {data?.years?.[2]}
                        </TableCell>
                    </TableRow>
                    <TableRow key={`head3-article-${index}`} className="smallPadding grayColor">
                        <TableCell align="left">
                            Ativo
                        </TableCell>
                        <TableCell align="center">
                            %
                        </TableCell>
                        <TableCell align="right">
                            R$
                        </TableCell>
                        <TableCell align="center">
                            %
                        </TableCell>
                        <TableCell align="right">
                            R$
                        </TableCell>
                        <TableCell align="center">
                            %
                        </TableCell>
                        <TableCell align="right">
                            R$
                        </TableCell>
                        <TableCell align="center">
                            %
                        </TableCell>
                        <TableCell align="right">
                            R$
                        </TableCell>

                    </TableRow>

                </TableHead>
            )
            nodes.push(
                <TableBody key={`body-article-${index}`}>
                    {assets.map((row, rowIndex) => {

                        return (
                            <TableRow key={`body-row-article-${index}-${rowIndex}`}>
                                <TableCell align="left" className={'ellipsis smallFont'}>
                                    {row.fund_name}
                                </TableCell>
                                <TableCell align="center" className="smallFont">
                                    {row.month.rentPercent}
                                </TableCell>
                                <TableCell align="right" className="smallFont">
                                    {row.month.rentMoney}
                                </TableCell>
                                <TableCell align="center" className="smallFont">
                                    {row.year1.rentPercent}
                                </TableCell>
                                <TableCell align="right" className="smallFont">
                                    {row.year1.rentMoney}
                                </TableCell>
                                <TableCell align="center" className="smallFont">
                                    {row.year2.rentPercent}
                                </TableCell>
                                <TableCell align="right" className="smallFont">
                                    {row.year2.rentMoney}
                                </TableCell>
                                <TableCell align="center" className="smallFont">
                                    {row.year3.rentPercent}
                                </TableCell>
                                <TableCell align="right" className="smallFont">
                                    {row.year3.rentMoney}
                                </TableCell>

                            </TableRow>

                        )
                    })}

                    <TableRow key={`target-row-article-${index}`} className="grayColor">
                        <TableCell align="left" className={'ellipsis smallFont'}>

                        </TableCell>
                        <TableCell align="center" className="smallFont">
                            {mapTargets?.month ?? NO_DATA}
                        </TableCell>
                        <TableCell align="right" className="smallFont">

                        </TableCell>
                        <TableCell align="center" className="smallFont">
                            {mapTargets?.yearBefore1 ?? NO_DATA}
                        </TableCell>
                        <TableCell align="right" className="smallFont">

                        </TableCell>
                        <TableCell align="center" className="smallFont">
                            {mapTargets?.yearBefore2 ?? NO_DATA}
                        </TableCell>
                        <TableCell align="right" className="smallFont">

                        </TableCell>
                        <TableCell align="center" className="smallFont">
                            {mapTargets?.yearBefore3 ?? NO_DATA}
                        </TableCell>
                        <TableCell align="right" className="smallFont">

                        </TableCell>

                    </TableRow>
                </TableBody>
            )
        });

        return nodes;
    }, [data, mapTargets])

    useEffect(() => {
        if (clientOn && clientOn.client && clientOn.client.id
            && clientOn.client.selectedPortfolioMonth && clientOn.client.selectedPortfolioYear) {

            let cancelled = false;

            setData([]);
            setMapTargets(null);
            setInnerLoad({
                loadMessage: "Carregando retornos por ativo...",
                size: "small"
            });
            loadData(clientOn.client, accountRegimeId, () => cancelled);

            return () => { cancelled = true; };

        } else {
            setInnerLoad({
                emptyMessage: "Os retornos se dão a partir do lançamento de pelo menos um mês de carteira",
            })

        }

    }, [clientOn, accountRegimeId]);

    return (
        <div className={classes.root}>

            <DefaultScrollWithHeaderContent innerLoad={innerLoad}>
                <MyTable
                    stickyHeader
                    size="small">
                    <colgroup>

                        <col width="15%" />

                    </colgroup>

                    {headAndBodyTable}

                </MyTable>
            </DefaultScrollWithHeaderContent>
        </div >
    );
})

export default AssetsTab;
